import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { PageActions } from "../../../../../shared/ui/PageActions";
import { looksLikeEmail } from "../../../../../shared/lib/looksLikeEmail";
import { useFocusOnMount } from "../../../../../shared/hooks/useFocusOnMount";
import { PageHead, PageShell } from "../../components/PageShell";
import { useTranslation } from "../../i18n/useTranslation";
import { sendPasswordReset } from "../../auth/emailAuth";
import { config } from "../../config/env";
import { isPossibleSlug } from "../../lib/slug";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import NotFound from "../NotFound";
import { StepMeta } from "./StepChrome";

type Phase = "form" | "sending" | "accepted" | "unknown" | "failed" | "limited";
function codeOf(error: unknown): string | null {
  return typeof error === "object" && error !== null && "code" in error && typeof error.code === "string" ? error.code : null;
}

// Firebase's existing app.* action handler owns the one-use link. This request
// never signs in/out, creates an account, or replaces Book's anonymous UID.
export default function BookRecovery() {
  const { slug } = useParams<{ slug: string }>();
  return isPossibleSlug(slug) ? <RecoveryForm key={slug} slug={slug} /> : <NotFound />;
}

function RecoveryForm({ slug }: { slug: string }) {
  const { t, locale } = useTranslation();
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>("form");
  const [invalid, setInvalid] = useState(false);
  const sending = useRef(false);
  const mounted = useRef(false);
  const field = useRef<HTMLInputElement>(null);
  usePageTitle(t(phase === "accepted" || phase === "unknown" ? "recovery.accepted" : "recovery.title"));
  useEffect(() => {
    mounted.current = true;
    field.current?.focus();
    return () => { mounted.current = false; };
  }, []);
  const heading = useFocusOnMount({
    on: [phase],
    when: phase === "accepted" || phase === "unknown" || phase === "failed" || phase === "limited",
  });

  const signIn = `/${slug}/book/signin`;
  const busy = phase === "sending";
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending.current || phase === "accepted" || phase === "unknown" || phase === "limited") return;
    const address = email.trim();
    if (!looksLikeEmail(address)) {
      setInvalid(true);
      field.current?.focus();
      return;
    }
    sending.current = true;
    setInvalid(false);
    setPhase("sending");
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // Canonical environment-correct URL, without email, token or booking data.
      await Promise.race([
        sendPasswordReset(address, locale, `https://${config.webHost}${signIn}`),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error("Reset request timed out")), 10_000); }),
      ]);
      if (mounted.current) setPhase("accepted");
    } catch (error) {
      if (mounted.current) {
        const code = codeOf(error);
        const refused = ["auth/invalid-email", "auth/unauthorized-continue-uri", "auth/invalid-continue-uri", "auth/operation-not-allowed", "auth/invalid-api-key"];
        setPhase(code === "auth/user-not-found" ? "accepted" : code === "auth/too-many-requests" ? "limited" : refused.includes(code ?? "") ? "failed" : "unknown");
      }
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      sending.current = false;
    }
  }
  return <PageShell testId="book-recovery" state={phase} legal={false} meta={<>
    {!busy && phase !== "accepted" && phase !== "unknown" ? <div className="b3-recovery-secondary">
      <Link className="b2-text" to={signIn}>{t("recovery.back")}</Link>
    </div> : null}
    <StepMeta />
  </>}>
    <PageHead title={t(phase === "accepted" || phase === "unknown" ? "recovery.accepted" : "recovery.title")} headingRef={heading} />
    {phase === "accepted" || phase === "unknown" ? <>
      <p className="b2-line">{t("recovery.request", { email: email.trim() })}</p>
      <PageActions className="b2-foot"><Link className="b-btn v-primary is-full" to={signIn}>{t("recovery.back")}</Link></PageActions>
    </> : <>
      {phase === "failed" || phase === "limited" ? <p id="recovery-error" className="b2-line t-bad" role="alert">{t(phase === "limited" ? "recovery.limited" : "recovery.failed")}</p> : null}
      <form id="book-recovery-form" className="b2-form" onSubmit={submit} noValidate>
        <div className="b-field"><label htmlFor="recovery-email">{t("recovery.email")}</label>
          <input id="recovery-email" ref={field} className="b-input" type="email" autoComplete="email" required value={email} disabled={busy || phase === "limited"}
            aria-invalid={invalid || undefined} aria-describedby={invalid ? "recovery-invalid" : phase === "failed" || phase === "limited" ? "recovery-error" : undefined}
            onChange={(event) => { setEmail(event.target.value); setInvalid(false); }} />
        </div>
        {invalid ? <p id="recovery-invalid" role="alert">{t("recovery.invalid")}</p> : null}
        <PageActions className="b2-foot"><button className="b-btn v-primary is-full" type="submit" form="book-recovery-form" disabled={busy || phase === "limited"} aria-busy={busy || undefined}>{t(busy ? "recovery.sending" : phase === "failed" ? "profile.error.retry" : "recovery.send")}</button></PageActions>
      </form>
    </>}
  </PageShell>;
}
